import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Flipper } from 'react-flip-toolkit';
import { useHistory } from 'react-router-dom';
import { toast } from 'sonner';
import { IEditAutomationTemplateRequest, ILocationState, IModalContext } from '../../../models';
import { EditAutomationTemplateItemIdPrefix, isSwitchStep } from '../../../models/Automations';
import { useErrorMessages } from '../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../models/Logging';
import {
	useAutomationTemplateSetNameMutation,
	useCommitAutomationDraftMutation,
	useDeleteAutomationTemplateMutation,
	useDiscardAutomationDraftMutation,
	useDuplicateAutomationTemplateMutation,
} from '../../../queries/Automation/mutations/template';
import { invalidateAutomationTemplateQuery } from '../../../queries/Automation/queries/useAutomationTemplateQuery';
import {
	AutomationDisableCancelModal,
	AutomationDisableCancelSelection,
} from '../../components/automation/AutomationDisableCancelModal';
import { AutomationTriggerCard } from '../../components/automation/AutomationTriggerCard';
import { EditAutomationTemplateItemBumpers } from '../../components/automation/EditAutomationTemplateItemBumpers';
import { EditAutomationTemplateSteps } from '../../components/automation/EditAutomationTemplateSteps';
import {
	ConfirmationDialog,
	DefaultDeleteConfirmationOptions,
	DefaultDiscardChangesConfirmationOptions,
	IConfirmationDialogOption,
} from '../../components/ConfirmationDialog';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { MultiContainerHeader } from '../../components/MultiContainerHeader';
import { NavigationBreadcrumbsBar } from '../../components/NavigationBreadcrumbsBar';
import { ScrollView } from '../../components/ScrollView';
import { AutomationTemplateStepIndicatorIcon } from '../../components/svgs/icons/AutomationTemplateStepIndicator';
import { DangerIcon } from '../../components/svgs/icons/DangerIcon';
import { LightningBoltIcon } from '../../components/svgs/icons/LightningBoltIcon';
import { PlusIcon } from '../../components/svgs/icons/PlusIcon';
import { TrashIcon } from '../../components/svgs/icons/TrashIcon';
import { WarningIcon } from '../../components/svgs/icons/WarningIcon';
import { TextInput } from '../../components/TextInput';
import { TextInputModal } from '../../components/TextInputModal';
import { baseStyleSheet } from '../../styles/styles';
import { INavigationItemProps } from '../MainContainer';
import { useAutomationEditorContext, useSegmentActions } from './hooks';
import { styleSheet } from './styles';

export interface IEditAutomationTemplateHeaderCallbacks {
	onDisableButtonClicked(): void;
	showDeleteConfirmation(): void;
}

interface IProps extends INavigationItemProps, IModalContext {
	automationTemplateId?: string;
	className?: string;
	hideDisableOption?: boolean;
	onRenderHeader?(callbacks: IEditAutomationTemplateHeaderCallbacks): React.ReactNode;
	readonly?: boolean;
	styles?: StyleDeclarationValue[];
	impersonationContext?: Api.IImpersonationContext;
	onAutomationDeleted: () => void;
	onAutomationPublished: () => void;
	onAutomationDisabled: () => void;
}

const PublishConfirmationOptions: IConfirmationDialogOption<boolean>[] = [
	{
		isCta: true,
		representedObject: true,
		title: 'Okay & Publish',
	},
];

export function EditAutomationTemplate(props: IProps) {
	const {
		routeContainerClassName,
		hideDisableOption,
		onRenderHeader,
		readonly,
		// TODO: Find where to get impersonationContext from - it's missing from several useCallback dep arrays below
		impersonationContext,

		onAutomationDeleted,
		onAutomationPublished,
		onAutomationDisabled,
	} = props;

	// Hooks
	const history = useHistory();
	const errorMessages = useErrorMessages();
	const { logEvent, logApiError } = useEventLogging('EditAutomationTemplate');

	// State
	const [loading, setLoading] = useState(true);
	const [name, setName] = useState<string | undefined>();
	const [lastSaveMoment, setLastSaveMoment] = useState<moment.Moment | undefined>();
	const [showDiscardChangesConfirmation, setShowDiscardChangesConfirmation] = useState(false);
	const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
	const [showDisableConfirmation, setShowDisableConfirmation] = useState(false);
	const [showDuplicationNameInputModal, setShowDuplicationNameInputModal] = useState(false);
	const [showPublishConfirmation, setShowPublishConfirmation] = useState(false);
	const [hasDraftVersion, setHasDraftVersion] = useState(false);

	// Refs
	const stepsContainerRef = useRef<Flipper>(null);
	// Track template ID to allow reload when template changes
	const currentTemplateIdRef = useRef<string | undefined>();

	// Custom hooks for step management - use derived templateId to avoid duplicate fetches
	const {
		steps,
		isLoading,
		addStep,
		removeStep,
		templateDataUpdatedAt,
		isTemplateError,
		templateError,
		getIncompleteSteps,
		automationTemplateId,
		automationTemplate,
	} = useAutomationEditorContext();

	const segmentActions = useSegmentActions({
		automationTemplateId: automationTemplate?.id || '',
		trigger:
			automationTemplate?.draft?.trigger || automationTemplate?.published?.trigger || ({} as Api.IAutomationTrigger),
		impersonationContext,
	});

	const addSegment = useCallback(
		async (deleteFirstEmptyStep?: boolean) => {
			const currentSteps = steps;
			const firstStep = currentSteps?.[0];
			const existingSwitchStep = currentSteps?.find(s => isSwitchStep(s)) as Api.ISwitchAutomationStep | undefined;

			if (deleteFirstEmptyStep && currentSteps?.length === 1 && !firstStep?.id) {
				await removeStep(firstStep);
			}

			await segmentActions.addSegment(existingSwitchStep);

			if (automationTemplate) {
				invalidateAutomationTemplateQuery(automationTemplate.id);
			}
		},
		[segmentActions, automationTemplate, steps, removeStep]
	);

	// Mutations
	const setNameMutation = useAutomationTemplateSetNameMutation({
		onSuccess: () => {
			setLastSaveMoment(moment());
			setHasDraftVersion(true);
		},
		onError: error => {
			logApiError('AutomationTemplateSetName-Error', error);
			errorMessages.pushApiError(error);
			setLoading(false);
		},
	});

	const commitDraftMutation = useCommitAutomationDraftMutation({
		onSuccess: () => {
			toast.success('Automation template published.');
			onAutomationPublished();
		},
		onError: error => {
			logApiError('CommitDraft-Error', error);
			errorMessages.pushApiError(error);
			setLoading(false);
		},
	});

	const discardDraftMutation = useDiscardAutomationDraftMutation({
		onSuccess: () => {
			toast.success('Pending changes were discarded.');
			invalidateAutomationTemplateQuery(automationTemplate.id);
			checkState(automationTemplate);
		},
		onError: error => {
			logApiError('Discard-Error', error);
			errorMessages.pushApiError(error);
			setLoading(false);
		},
	});

	const deleteTemplateMutation = useDeleteAutomationTemplateMutation({
		onSuccess: () => {
			toast.success('Automation template deleted.');
			onAutomationDeleted();
		},
		onError: error => {
			logApiError('Delete-Error', error);
			errorMessages.pushApiError(error);
			setLoading(false);
		},
	});

	const duplicateTemplateMutation = useDuplicateAutomationTemplateMutation({
		onSuccess: createdTemplate => {
			const locationState: ILocationState<Api.IAutomationTemplate, IEditAutomationTemplateRequest> = {
				model: {
					automationTemplate: createdTemplate,
				},
			};
			history?.replace(`/Automations/${createdTemplate.id}`, locationState);
			invalidateAutomationTemplateQuery(createdTemplate.id);
			checkState(createdTemplate);
		},
		onError: error => {
			logApiError('DuplicateAutomationTemplate-Error', error);
			errorMessages.pushApiError(error);
		},
	});

	// TODO: Move this to the context
	const canEdit = useMemo(() => {
		if (readonly) return false;
		if (automationTemplate?.status === Api.TemplateStatus.Archived) return false;
		if (automationTemplate?.scope === Api.TemplateScope.Industry) return false;
		return true;
	}, [readonly, automationTemplate?.status, automationTemplate?.scope]);

	// TODO: Move this to the context
	const isUpdatingStep = useMemo(() => {
		// TODO: Track updates
		return false;
		// return (steps || []).filter(x => !!x).some(x => x?.isBusy);
	}, []);

	const checkState = (template: Api.IAutomationTemplate) => {
		setName(template.name);
		setHasDraftVersion(!!template.draft);
		setLoading(false);
	};

	// Watch for query data and initialize template when available
	const hasInitializedRef = useRef(false);

	// Track the last processed dataUpdatedAt to detect actual data changes
	const lastTemplateDataUpdatedAtRef = useRef<number | undefined>();

	useEffect(() => {
		// Load template when query data becomes available or template changes
		const templateData = automationTemplate;
		const dataUpdatedAt = templateDataUpdatedAt;

		const templateChanged = currentTemplateIdRef.current !== templateData?.id;
		const dataRefreshed = dataUpdatedAt && lastTemplateDataUpdatedAtRef.current !== dataUpdatedAt;

		if (templateData && !isLoading) {
			if (!hasInitializedRef.current || templateChanged || dataRefreshed) {
				hasInitializedRef.current = true;
				currentTemplateIdRef.current = templateData.id;
				lastTemplateDataUpdatedAtRef.current = dataUpdatedAt;
				checkState(templateData);
			}
		}
	}, [automationTemplate, isLoading, templateDataUpdatedAt]);

	// Handle query errors
	useEffect(() => {
		if (isTemplateError && templateError) {
			logApiError('AutomationTemplateLoad-Error', templateError as Api.IOperationResultNoValue);
			errorMessages.pushApiError(templateError as Api.IOperationResultNoValue);
			setLoading(false);
		}
	}, [isTemplateError, templateError, logApiError, errorMessages]);

	// Handle case where no templateId is available
	useEffect(() => {
		if (!automationTemplateId && !automationTemplate) {
			setLoading(false);
			errorMessages.push({
				messages: ['Unable to load. An automation template id is required.'],
			});
		}
	}, [automationTemplateId, automationTemplate, errorMessages]);

	// Handlers
	const onAddStepClicked = useCallback(() => {
		addStep();
	}, [addStep]);

	const onAddSegmentClicked = useCallback(async () => {
		try {
			const firstStep = steps?.[0];
			const deleteFirstEmptyStep = steps?.length === 1 && !firstStep?.id;
			await addSegment(deleteFirstEmptyStep);
		} catch (error) {
			logApiError('AddSegment-Error', error);
			errorMessages.pushApiError(error);
		}
	}, [steps, addSegment, logApiError, errorMessages]);

	const onStepSaved = useCallback(() => {
		setLastSaveMoment(moment());
		setHasDraftVersion(true);
	}, []);

	const onNameInputChanged = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setName(e.target.value);
	}, []);

	const onNameInputBlur = useCallback(() => {
		if (!name || !automationTemplate?.id) {
			return;
		}
		logEvent('AutomationTemplateSetName', { id: automationTemplate.id });
		setNameMutation.mutate({
			id: automationTemplate.id,
			name,
			impersonationContext,
		});
	}, [name, automationTemplate, logEvent, setNameMutation, impersonationContext]);

	const onPublishButtonClicked = useCallback(() => {
		setShowPublishConfirmation(true);
	}, []);

	const publish = useCallback(async () => {
		if (!automationTemplate?.id) {
			errorMessages.push({
				messages: ['Please save your automation before enabling it.'],
			});
			return;
		}

		try {
			// Look for errors and scroll to them
			const incompleteSteps = await getIncompleteSteps();
			const firstIncompleteStep = incompleteSteps[0];
			if (firstIncompleteStep) {
				toast.error('Please complete all required fields before publishing.');
				const elementId = `${EditAutomationTemplateItemIdPrefix}-${firstIncompleteStep.id}`;
				document.getElementById(elementId)?.scrollIntoView({ behavior: 'smooth' });
				return;
			}

			setLoading(true);
			logEvent('CommitDraft', { id: automationTemplate.id });
			commitDraftMutation.mutate({
				id: automationTemplate.id,
				impersonationContext,
			});
		} catch (error) {
			logApiError('Publish-Error', error as Api.IOperationResultNoValue);
			errorMessages.push({
				messages: ['An error occurred while publishing the automation.'],
			});
		}
	}, [
		automationTemplate,
		logEvent,
		logApiError,
		commitDraftMutation,
		errorMessages,
		getIncompleteSteps,
		impersonationContext,
	]);

	const onPublishConfirmationDialogRequestClose = useCallback(
		(result?: IConfirmationDialogOption<boolean>, cancel?: boolean) => {
			setShowPublishConfirmation(false);
			if (!!result?.representedObject && !cancel) {
				publish();
			}
		},
		[publish]
	);

	const showDiscardChanges = useCallback(() => {
		setShowDiscardChangesConfirmation(true);
	}, []);

	const onDiscardChangesConfirmationDialogRequestClose = useCallback(
		(result?: IConfirmationDialogOption<boolean>, cancel?: boolean) => {
			setShowDiscardChangesConfirmation(false);
			if (!!result?.isDestructive && !cancel && automationTemplate?.id) {
				setLoading(true);
				logEvent('DiscardChanges', { id: automationTemplate.id });
				discardDraftMutation.mutate({
					id: automationTemplate.id,
					impersonationContext,
				});
			}
		},
		[automationTemplate, logEvent, discardDraftMutation, impersonationContext]
	);

	const showDeleteConfirmationHandler = useCallback(() => {
		setShowDeleteConfirmation(true);
	}, []);

	const onDeleteConfirmationDialogRequestClose = useCallback(
		(result?: IConfirmationDialogOption<boolean>, cancel?: boolean) => {
			setShowDeleteConfirmation(false);
			if (!!result?.isDestructive && !cancel && automationTemplate?.id) {
				setLoading(true);
				logEvent('Delete', { id: automationTemplate.id });
				deleteTemplateMutation.mutate({
					id: automationTemplate.id,
					impersonationContext,
				});
			}
		},
		[automationTemplate, logEvent, deleteTemplateMutation, impersonationContext]
	);

	const onDisableButtonClicked = useCallback(() => {
		if (automationTemplate?.stats?.inProgress >= 0) {
			setShowDisableConfirmation(true);
		}
	}, [automationTemplate]);

	const onDisableModalClosed = useCallback(
		(result: AutomationDisableCancelSelection) => {
			setShowDisableConfirmation(false);
			if (result !== AutomationDisableCancelSelection.DoNothing) {
				onAutomationDisabled();
			}
		},
		[onAutomationDisabled]
	);

	const onSaveAsNewClicked = useCallback(() => {
		setShowDuplicationNameInputModal(true);
	}, []);

	const onDuplicateAutomationNameInputModalRequestClose = useCallback(
		(result?: string, cancel?: boolean) => {
			setShowDuplicationNameInputModal(false);
			if (!cancel && !!result && automationTemplate?.id) {
				logEvent('DuplicateAutomation', {
					id: automationTemplate.id,
					nameLength: result.length,
				});
				duplicateTemplateMutation.mutate({
					id: automationTemplate.id,
					name: result,
					impersonationContext,
				});
			}
		},
		[automationTemplate, logEvent, duplicateTemplateMutation, impersonationContext]
	);

	// Render helpers
	const renderTriggerIndicator = useCallback(() => {
		return (
			<div className={css(styleSheet.stepIndicator)}>
				<AutomationTemplateStepIndicatorIcon disableInnerCircle={true} className={css(styleSheet.triggerIndicator)}>
					<LightningBoltIcon className={css(styleSheet.triggerIndicatorIcon)} />
				</AutomationTemplateStepIndicatorIcon>
				<div className={css(styleSheet.triggerIndicatorHorizontalLine)} />
				<div className={css(styleSheet.triggerIndicatorVerticalLine)} />
			</div>
		);
	}, []);

	const renderStepsFooter = useCallback(() => {
		if (!canEdit) {
			return null;
		}

		const addStepButton = (
			<button className={css(baseStyleSheet.horizontalStack, styleSheet.addStepButton)} onClick={onAddStepClicked}>
				<PlusIcon />
				<span>Add a Step</span>
			</button>
		);

		const renderAddSegmentButton = (title?: string) => (
			<button className={css(baseStyleSheet.horizontalStack, styleSheet.addStepButton)} onClick={onAddSegmentClicked}>
				<PlusIcon />
				<span>{title || 'Add Segment'}</span>
			</button>
		);

		return (
			<div className={css(baseStyleSheet.horizontalStack, styleSheet.stepsFooter)}>
				{steps?.every(x => !!x._type && !isSwitchStep(x)) || steps?.length === 0 ? (
					<>
						{addStepButton}
						{renderAddSegmentButton()}
					</>
				) : steps?.length === 1 && !steps[0]?.id ? (
					renderAddSegmentButton('Add Segment Instead')
				) : steps?.some(x => isSwitchStep(x)) ? (
					renderAddSegmentButton()
				) : null}
			</div>
		);
	}, [canEdit, steps, onAddStepClicked, onAddSegmentClicked]);

	const renderAutosaveInfo = useCallback(() => {
		return (
			<div className={css(styleSheet.autosaveInfo)}>
				{!!lastSaveMoment && `Auto saved on ${lastSaveMoment.format('MM/DD/YYYY hh:mma')}`}
			</div>
		);
	}, [lastSaveMoment]);

	const renderPublishButton = useCallback(() => {
		const isDisabled = isUpdatingStep || steps?.some(x => !x?.id) || steps?.length === 0 || !hasDraftVersion || !name;

		if (automationTemplate?.scope === Api.TemplateScope.Industry && !canEdit && !hasDraftVersion) {
			return (
				<>
					<button className={css(baseStyleSheet.ctaButton)} onClick={onSaveAsNewClicked}>
						<span>Save as New Template</span>
					</button>
					{!!showDuplicationNameInputModal && (
						<TextInputModal
							cta='Duplicate'
							modalProps={{
								isOpen: !!showDuplicationNameInputModal,
								onRequestClose: onDuplicateAutomationNameInputModalRequestClose,
							}}
							placeholderText='New Automation name'
							title={`Duplicate Automation: "${automationTemplate?.name}"`}
						/>
					)}
				</>
			);
		}

		if (canEdit) {
			return (
				<button
					className={css(automationTemplate?.published ? baseStyleSheet.ctaButton : styleSheet.enableButton)}
					disabled={isDisabled}
					onClick={!isDisabled ? onPublishButtonClicked : undefined}
				>
					<span>
						{!!automationTemplate?.published && automationTemplate?.status !== Api.TemplateStatus.Disabled
							? 'Publish Changes'
							: 'Enable Automation'}
					</span>
				</button>
			);
		}

		return null;
	}, [
		automationTemplate,
		isUpdatingStep,
		steps,
		hasDraftVersion,
		name,
		canEdit,
		showDuplicationNameInputModal,
		onSaveAsNewClicked,
		onDuplicateAutomationNameInputModalRequestClose,
		onPublishButtonClicked,
	]);

	const renderHeader = useCallback(() => {
		return (
			<div className={css(styleSheet.headerFooter)}>
				{renderAutosaveInfo()}
				<div className={css(baseStyleSheet.horizontalStack)}>
					{!loading &&
						(onRenderHeader ? (
							onRenderHeader({
								onDisableButtonClicked,
								showDeleteConfirmation: showDeleteConfirmationHandler,
							})
						) : (
							<>
								{hasDraftVersion && (
									<div className={css(styleSheet.unpublished)}>
										<DangerIcon className={css(styleSheet.dangerIcon)} />
										unpublished changes
									</div>
								)}
								{renderPublishButton()}
								{!!canEdit && !!automationTemplate?.published && hasDraftVersion && (
									<button
										className={css(baseStyleSheet.horizontalStack, baseStyleSheet.ctaButtonDestructiveSolid)}
										disabled={isUpdatingStep}
										onClick={showDiscardChanges}
									>
										<span>Discard Changes</span>
									</button>
								)}
								{!!canEdit && (
									<>
										{!!automationTemplate?.published &&
											automationTemplate?.status !== Api.TemplateStatus.Disabled &&
											!hideDisableOption && (
												<button
													className={css(baseStyleSheet.ctaButtonDestructive)}
													disabled={isUpdatingStep}
													onClick={onDisableButtonClicked}
												>
													<span>Disable Automation</span>
												</button>
											)}
										<button
											className={css(baseStyleSheet.horizontalStack, baseStyleSheet.ctaButtonDestructive)}
											disabled={isUpdatingStep}
											onClick={showDeleteConfirmationHandler}
										>
											<TrashIcon />
											<span>Delete</span>
										</button>
									</>
								)}
							</>
						))}
				</div>
			</div>
		);
	}, [
		renderAutosaveInfo,
		loading,
		onRenderHeader,
		onDisableButtonClicked,
		showDeleteConfirmationHandler,
		hasDraftVersion,
		renderPublishButton,
		automationTemplate,
		isUpdatingStep,
		showDiscardChanges,
		canEdit,
		hideDisableOption,
	]);

	const locationName = loading
		? 'Loading...'
		: `${automationTemplate?.published?.steps?.length > 0 ? 'Edit' : 'Create New'} Automation`;

	return (
		<ScrollView
			className={`${css(styleSheet.scrollView)} edit-automation-template `}
			contentClassName={routeContainerClassName || undefined}
			hasFixedHeader={true}
			header={renderHeader()}
		>
			<MultiContainerHeader
				appBarHeader={<NavigationBreadcrumbsBar currentLocationName={locationName} />}
				fullscreenHeader={locationName}
			/>
			<div className={css(styleSheet.body)}>
				<div className={css(styleSheet.bodyBackground)} />
				<div className={css(styleSheet.bodyContent)}>
					<article className={css(styleSheet.nameContainer)}>
						<h2 className={css(styleSheet.sectionTitle)}>
							Automation Name: &nbsp;
							<span className={css(baseStyleSheet.required)}>*</span>
						</h2>
						<TextInput
							className={css(styleSheet.nameInput)}
							disabled={loading || !canEdit}
							inputId='edit-automation-template-name-input'
							onBlur={onNameInputBlur}
							onChange={onNameInputChanged}
							type='text'
							value={name || ''}
						/>
						<div className={css(styleSheet.nameInputHint)}>
							Example: &quot;Leads Email Drip&quot;, or &quot;New Client To Do List&quot;
						</div>
					</article>
					<EditAutomationTemplateItemBumpers onRenderLeftBumperContent={renderTriggerIndicator}>
						<AutomationTriggerCard
							automationTemplate={automationTemplate as any}
							impersonationContext={impersonationContext}
							steps={steps}
						/>
					</EditAutomationTemplateItemBumpers>
					<EditAutomationTemplateSteps
						ref={stepsContainerRef}
						onStepSaved={onStepSaved}
						automationTemplate={automationTemplate as any}
						impersonationContext={impersonationContext}
					>
						{renderStepsFooter()}
					</EditAutomationTemplateSteps>
					{loading && <LoadingSpinner className={css(baseStyleSheet.absoluteCenter)} type='large' />}
				</div>
			</div>
			{!!canEdit && (
				<>
					{!!showDeleteConfirmation && (
						<ConfirmationDialog
							icon={<WarningIcon />}
							modalProps={{
								isOpen: !!showDeleteConfirmation,
								onRequestClose: onDeleteConfirmationDialogRequestClose,
							}}
							options={DefaultDeleteConfirmationOptions}
							title='Are you sure you want to delete this automation template?'
						/>
					)}
					{!!showPublishConfirmation && (
						<ConfirmationDialog
							icon={<WarningIcon />}
							modalProps={{
								isOpen: !!showPublishConfirmation,
								onRequestClose: onPublishConfirmationDialogRequestClose,
							}}
							options={PublishConfirmationOptions}
							title={
								<span>
									Changes will apply to future
									<br />
									automations in progress.
								</span>
							}
						>
							<div style={{ textAlign: 'center' }}>
								Contacts that have existing automation in progress
								<br />
								will finish out the previous version.
							</div>
						</ConfirmationDialog>
					)}
					{!!showDiscardChangesConfirmation && (
						<ConfirmationDialog
							icon={<WarningIcon />}
							modalProps={{
								isOpen: !!showDiscardChangesConfirmation,
								onRequestClose: onDiscardChangesConfirmationDialogRequestClose,
							}}
							options={DefaultDiscardChangesConfirmationOptions}
							title='Are you sure you want to discard changes?'
						/>
					)}
					{!!showDisableConfirmation && (
						<AutomationDisableCancelModal
							automationTemplate={automationTemplate as any}
							impersonationContext={impersonationContext}
							isOpen={showDisableConfirmation}
							onRequestClose={onDisableModalClosed}
						/>
					)}
				</>
			)}
		</ScrollView>
	);
}
